import { computed, ref } from 'vue'
import gql from 'graphql-tag'
import { useMutation, useQuery } from '@vue/apollo-composable'

import type {
  File,
  SetRecognizedOriginsInput,
  EstimateRegistrationCostQuery,
  EstimateRegistrationCostQueryVariables,
} from '@/shared/graphql/router/graphql'
import { useOriginStore } from '@/shared/stores/origin'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { formatPrice } from '@/shared/utils/money'

const SET_RECOGNIZED_ORIGINS = gql`
  mutation SetRecognizedOrigins($input: SetRecognizedOriginsInput!) {
    setRecognizedOrigins(input: $input) {
      files {
        id
      }
    }
  }
`

const ESTIMATE_REGISTRATION_COST = gql`
  query EstimateRegistrationCost($input: EstimateRegistrationCostInput!) {
    estimateRegistrationCost(input: $input) {
      total {
        amount
        formatted
      }
      breakdown {
        description
        origin {
          id
          name
          flag {
            url
          }
        }
        total {
          amount
          formatted
        }
        breakdown {
          id
          name
          total {
            amount
            formatted
          }
        }
      }
    }
  }
`

export function useConfirmRegisterCountries(
  files: File[],
  originIds: string[],
  closeAll: () => void
) {
  const originStore = useOriginStore()
  const toastStore = useToastStore()

  const workCount = files.length

  const selectedOrigins = computed(() =>
    originStore.origins.filter((origin) => originIds.includes(origin.id))
  )

  const { result } = useQuery<
    EstimateRegistrationCostQuery,
    EstimateRegistrationCostQueryVariables
  >(
    ESTIMATE_REGISTRATION_COST,
    () => ({
      input: {
        origins: [...originIds],
        quantity: workCount,
      },
    }),
    { clientId: 'router' }
  )

  const estimatedCost = computed(
    () => result.value?.estimateRegistrationCost ?? null
  )

  const totalCost = computed(() =>
    formatPrice({ amount: estimatedCost.value?.total.amount || 0 })
  )

  const agreeToTerms = ref(false)

  const { mutate, loading } = useMutation<
    { setRecognizedOrigins: { files: { id: string }[] } },
    { input: SetRecognizedOriginsInput }
  >(SET_RECOGNIZED_ORIGINS, { clientId: 'router' })

  const disableButton = computed(
    () => !agreeToTerms.value || !estimatedCost.value || loading.value
  )

  const save = async () => {
    if (disableButton.value) return
    try {
      await mutate({
        input: {
          fileIds: files.map((file) => file.id),
          originIds,
        },
      })
      toastStore.showToast({
        type: ToastType.Success,
        content: 'Registration in additional jurisdictions has started.',
      })
      closeAll()
    } catch (error) {
      if (error instanceof Error) {
        toastStore.showToast({
          type: ToastType.Error,
          content:
            'Could not register additional jurisdictions — please try again later.',
          error,
        })
      }
    }
  }

  return {
    workCount,
    selectedOrigins,
    estimatedCost,
    totalCost,
    agreeToTerms,
    disableButton,
    loading,
    save,
  }
}
